/**
 * Alert Settings keyword monitor section with Keystone-style Suggested Keywords + Auto-match.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, useToastStore, getAPIClient } from '@tracekit/core';
import { Sparkles, Zap } from 'lucide-react';
import {
  getRecommendedKeywords,
  getKeywordTaskStatus,
  type RecommendedKeywordsResponse,
} from '../api/opportunities';
import { KeywordRecommendationsModal } from './government-data/KeywordRecommendationsModal';
import {
  DEFAULT_AUTO_MATCH_KEYWORD_LIMIT,
  extractTopMonitorKeywords,
  getUnselectedSuggestionChips,
  mergeMonitorKeywords,
} from '../utils/alertKeywordRecommendations';
import { SPARC_RFP_DEFAULT_KEYWORDS } from '../services/rssFeedRegistry';

interface AlertKeywordSettingsSectionProps {
  projectId: string;
  selectedKeywords: string[];
  onSelectedKeywordsChange: (keywords: string[]) => void;
  labelClassName?: string;
  enableDefaultAutoMatch?: boolean;
}

async function resolveRecommendedKeywords(
  projectId: string,
  bypassCache = false
): Promise<RecommendedKeywordsResponse> {
  const initial = await getRecommendedKeywords(projectId, bypassCache);
  if (initial.cached || (initial.keywords?.length ?? 0) > 0 || !initial.task_id) {
    return initial;
  }

  const taskId = initial.task_id;
  const maxAttempts = 120;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const status = await getKeywordTaskStatus(taskId);
    if (status.status === 'completed') {
      return {
        keywords: status.keywords ?? [],
        total_count: status.total_count ?? status.keywords?.length ?? 0,
        location_keywords: status.location_keywords,
        department_names: status.department_names,
        location_count: status.location_count,
        department_count: status.department_count,
        location_keywords_with_count: status.location_keywords_with_count,
        department_names_with_count: status.department_names_with_count,
        total_opportunities_analyzed: status.total_opportunities_analyzed,
        service_keywords: status.service_keywords,
        service_keywords_count: status.service_keywords_count,
        service_keyword_groups: status.service_keyword_groups,
        cached: true,
      };
    }
    if (status.status === 'failed') {
      throw new Error(status.error || status.message || 'Keyword generation failed');
    }
  }

  throw new Error('Keyword generation timed out');
}

function keywordsMatchDefaults(selectedKeywords: string[]): boolean {
  if (selectedKeywords.length !== SPARC_RFP_DEFAULT_KEYWORDS.length) return false;
  const selected = new Set(selectedKeywords.map((k) => k.trim().toLowerCase()));
  return SPARC_RFP_DEFAULT_KEYWORDS.every((k) => selected.has(k.trim().toLowerCase()));
}

export function AlertKeywordSettingsSection({
  projectId,
  selectedKeywords,
  onSelectedKeywordsChange,
  labelClassName = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2',
  enableDefaultAutoMatch = true,
}: AlertKeywordSettingsSectionProps) {
  const { addToast } = useToastStore();
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const autoMatchRunRef = useRef<string | null>(null);

  const { data: keywordsData, refetch: refetchKeywords } = useQuery({
    queryKey: ['recommended-keywords', projectId],
    queryFn: () => getRecommendedKeywords(projectId),
    enabled: !!projectId,
    staleTime: 30 * 60 * 1000,
  });

  const { data: rankingStats } = useQuery({
    queryKey: ['opportunities-ranking-stats', projectId],
    queryFn: async () => {
      const api = getAPIClient();
      return api.get<{ total_opportunities: number; ranked_count: number }>(
        `/api/opportunities/ranking-stats?project_id=${projectId}`
      );
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const suggestionChips = getUnselectedSuggestionChips(keywordsData, selectedKeywords);

  const applyRecommendedKeywords = useCallback(
    async (options?: { bypassCache?: boolean; silent?: boolean }) => {
      if (!projectId) return 0;

      setIsAutoMatching(true);
      try {
        const response = options?.bypassCache
          ? await resolveRecommendedKeywords(projectId, true)
          : keywordsData && (keywordsData.keywords?.length ?? 0) > 0
            ? keywordsData
            : await resolveRecommendedKeywords(projectId, false);

        const topKeywords = extractTopMonitorKeywords(response, DEFAULT_AUTO_MATCH_KEYWORD_LIMIT);
        if (topKeywords.length === 0) {
          if (!options?.silent) {
            addToast({
              type: 'warning',
              title: 'No keyword recommendations',
              message: 'Try refreshing suggested keywords from your project profile.',
            });
          }
          return 0;
        }

        const merged = mergeMonitorKeywords(selectedKeywords, topKeywords);
        const addedCount = merged.length - selectedKeywords.length;
        onSelectedKeywordsChange(merged);

        if (!options?.silent && addedCount > 0) {
          addToast({
            type: 'success',
            title: 'Keywords updated',
            message: `Added ${addedCount} SPARC-relevant keyword${addedCount === 1 ? '' : 's'} to your alert monitor list.`,
          });
        }

        if (options?.bypassCache) {
          await refetchKeywords();
        }

        return addedCount;
      } catch (error) {
        if (!options?.silent) {
          addToast({
            type: 'error',
            title: 'Auto-match failed',
            message: error instanceof Error ? error.message : 'Unable to load recommended keywords',
          });
        }
        return 0;
      } finally {
        setIsAutoMatching(false);
      }
    },
    [addToast, keywordsData, onSelectedKeywordsChange, projectId, refetchKeywords, selectedKeywords]
  );

  const handleAutoMatch = useCallback(async () => {
    const key = `${projectId}-${selectedKeywords.length}`;
    if (autoMatchRunRef.current === key && isAutoMatching) return;
    autoMatchRunRef.current = key;
    await applyRecommendedKeywords();
  }, [applyRecommendedKeywords, isAutoMatching, projectId, selectedKeywords.length]);

  useEffect(() => {
    if (!enableDefaultAutoMatch || !projectId || !keywordsData) return;
    if (!keywordsMatchDefaults(selectedKeywords)) return;

    const key = `${projectId}-defaults`;
    if (autoMatchRunRef.current === key) return;

    const topKeywords = extractTopMonitorKeywords(keywordsData);
    if (topKeywords.length === 0) return;

    autoMatchRunRef.current = key;
    void applyRecommendedKeywords({ silent: true });
  }, [
    applyRecommendedKeywords,
    enableDefaultAutoMatch,
    keywordsData,
    projectId,
    selectedKeywords,
  ]);

  const addKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    const exists = selectedKeywords.some(
      (existing) => existing.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return;
    onSelectedKeywordsChange([...selectedKeywords, trimmed]);
  };

  const removeKeyword = (keyword: string) => {
    onSelectedKeywordsChange(selectedKeywords.filter((k) => k !== keyword));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <label className={labelClassName}>Keywords to Monitor</label>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowKeywordModal(true)}
            className="flex items-center gap-1.5"
            title="Browse AI-recommended keywords for your project"
          >
            <Sparkles className="h-4 w-4" />
            Suggested Keywords
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoMatch}
            disabled={isAutoMatching}
            className="flex items-center gap-1.5"
            title="Apply top SPARC-relevant keywords from your project profile"
          >
            <Zap className={`h-4 w-4 ${isAutoMatching ? 'animate-pulse' : ''}`} />
            {isAutoMatching ? 'Matching...' : 'Auto-match'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {selectedKeywords.map((keyword) => (
          <Badge key={keyword} variant="primary" className="cursor-pointer flex items-center gap-1">
            {keyword}
            <button
              type="button"
              onClick={() => removeKeyword(keyword)}
              className="hover:text-red-600 text-sm"
              aria-label={`Remove keyword ${keyword}`}
            >
              ×
            </button>
          </Badge>
        ))}
        <input
          type="text"
          placeholder="Add keyword..."
          className="px-3 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              const input = e.currentTarget;
              addKeyword(input.value);
              input.value = '';
            }
          }}
        />
      </div>

      {suggestionChips.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick add from recommendations</p>
          <div className="flex flex-wrap gap-2">
            {suggestionChips.map((chip) => (
              <button
                key={chip.keyword}
                type="button"
                onClick={() => addKeyword(chip.keyword)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                title={
                  chip.confidence_pct != null
                    ? `${chip.keyword} — ${chip.confidence_pct}% confidence`
                    : chip.keyword
                }
              >
                <Sparkles className="h-3 w-3" />
                {chip.keyword}
                {chip.confidence_pct != null && (
                  <span className="text-blue-500/80">({chip.confidence_pct}%)</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {rankingStats && rankingStats.total_opportunities > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Project context: {rankingStats.ranked_count} of {rankingStats.total_opportunities} opportunities
          ranked — recommendations use the same project profile as Opportunities.
        </p>
      )}

      <KeywordRecommendationsModal
        isOpen={showKeywordModal}
        onClose={() => setShowKeywordModal(false)}
        onSelectKeyword={addKeyword}
      />
    </div>
  );
}
